import request from "@/utils/http"
import { isValidObjectId } from "@/utils/common"
import { Message } from "element-ui"
import * as templateApi from "@/api/public/template"
import store from "@/store"

/**
 * 获取数据详情,用于前端详情展示
 * 与普通获取数据详情相比,不填充关联多条,关联填充深度为1
 * @param dataId
 * @param appId
 * @returns {AxiosPromise<any>}
 */
export function getSheetDataForDetail({ dataId, appId }) {
  if (store.getters.isTemplateMode) {
    return templateApi.getSheetDataForDetail({ dataId, appId })
  }
  if (!isValidObjectId(dataId)) {
    Message.error("数据不存在")
    return Promise.reject("数据不存在")
  }
  return request
    .get(`/sheet/data/getSheetDataForDetail`, { params: { dataId, appId } })
    .commonDeal()
}

export function getSheetDataById(sheetId, dataId) {
  if (store.getters.isTemplateMode) {
    return templateApi.getSheetDataById(sheetId, dataId)
  }
  return request.get("/sheet/getSheetDataById", { params: { sheetId, dataId } }).commonDeal()
}

/**
 * 获取数据(用于编辑)
 * appId 和 sheetId 最好都传.(sheetId只有当预览模板时有用)
 * @param dataId
 * @param appId
 * @param withDetail
 * @param withString
 * @param sheetId
 * @returns {Promise<*>|*}
 */
export function getSheetDataForEdit({
  dataId,
  appId,
  withDetail = true,
  withString = true,
  sheetId
}) {
  if (store.getters.isTemplateMode) {
    return templateApi.getSheetDataById(sheetId, dataId)
  }
  return request
    .post("/sheet/getSheetDataForEdit", {
      appId,
      sheetDataId: dataId,
      withDetail,
      withDataForShow: withString
    })
    .commonDeal()
}

export function updateSheetData(sheetId, dataId, version, data) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request
    .post(`/sheet/data/update/${sheetId}/${dataId}`, data, { params: { version } })
    .commonDeal()
}

export function insertSheetData(sheetId, data) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request.post(`/sheet/data/insert/${sheetId}`, data).commonDeal()
}

/**
 * @param dataId
 * @param appId 非必填
 * @returns {AxiosPromise<any>}
 */
export function deleteSheetData({ dataId, appId }) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request.post(`/sheet/data/delete`, null, { params: { dataId, appId } }).commonDeal()
}

export function getDataLogs({ dataId, appId, lastLogId }) {
  if (store.getters.isTemplateMode) {
    return Promise.resolve([])
  }
  return request.get("/sheet/getDataLogs", { params: { dataId, appId, lastLogId } }).commonDeal()
}

export function getSelect2Data(sheetId, cid, key) {
  return request.get(`/sheet/select2/sheetId/${sheetId}/cid/${cid}/key/${key}`)
}

/**
 * API - 锁定/解锁 工作表记录
 * @param {String} sheetId
 * @param {String} dataId
 * @param {Boolean} locked 是否被锁定，true代表已锁定，false代表未锁定
 */
export function lockOrUnlockSheetData(sheetId, dataId, locked) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request.get(`/sheet/data/${locked ? "unlock" : "lock"}/${sheetId}/${dataId}`).commonDeal()
}
/**
 * API - 更新参与者(添加/删除)
 * @param {String} sheetId
 * @param {String} dataId
 * @param {Array} userIds
 */
export function updateParticipants(sheetId, dataId, userIds) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request.post(`/sheet/data/part/update/${sheetId}/${dataId}`, userIds)
}

/**
 * API - 更新负责人
 * @param {String} sheetId
 * @param {String} dataId
 * @param {String} newOwnerId
 * @param {Boolean} partFlag 是否转成参与者，true代表转成参与者
 */
export function updateOwner(sheetId, dataId, newOwnerId, partFlag = true) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request
    .post(`/sheet/data/owner/update/${sheetId}/${dataId}/${newOwnerId}?partFlag=${partFlag}`)
    .commonDeal()
}

/**
 * @param type add/delete
 * @param dataId
 * @param appId 非必填
 * @param refCid
 * @param refDataIds id数组,逗号分隔
 * @returns {*}
 */
export function updateRefData({ type, dataId, appId, refCid, refDataIds }) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request
    .post(`/sheet/data/updateRefData`, null, {
      params: { type, dataId, appId, refCid, refDataIds }
    })
    .commonDeal()
}

export function setSheetDataProject(sheetId, sheetDataId, projectId) {
  if (store.getters.isTemplateMode) {
    Message.warning("预览状态无法操作")
    return Promise.reject("预览状态无法操作")
  }
  return request
    .post(`/sheet/setSheetDataProject`, null, {
      params: { sheetId, sheetDataId, projectId }
    })
    .commonDeal()
}

// 发送验证码
export function sendVerifyCode(cid, sheetId, data) {
  return request.post(`/sheet/data/sendVerifyCode`, data, { params: { cid, sheetId } }).commonDeal()
}

// 附件压缩
export function createZipFile(attachmentIds,addWaterMark) {
  return request.post(`/sheet/createZipFile`, {"attachmentIds":attachmentIds,"addWaterMark":addWaterMark}).commonDeal()
}
export function fileWatermark(attachmentId) {
  return request.post(`/sheet/createWatermarksFile`, null,{ params: { attachmentId } }).commonDeal()
}
